import React, { useEffect, useState } from "react";
import "./css/demo_accounts.scss";

import AdminService from "../../services/admin_service";

/**
 * System Admin — Demo Accounts.
 *
 * Clone a fresh private demo (own login + a copy of the template account's loan
 * files) for each new hire, and reassign an account (new email / password) when
 * someone leaves so it can be reused.
 */
export default function DemoAccounts() {
  const [accounts, setAccounts] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    templateEmail: "",
    assignedLender: "",
    isDemo: true
  });

  const [reassignId, setReassignId] = useState(null);
  const [reassign, setReassign] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    assignedLender: "",
    isDemo: false
  });

  function load() {
    AdminService.listDemoAccounts()
      .then(list => setAccounts(Array.isArray(list) ? list : []))
      .catch(e => setError(String(e && e.message ? e.message : e)));
  }
  useEffect(load, []);

  async function submitClone(e) {
    e.preventDefault();
    setError("");
    if (!form.email || !form.password) {
      setError("Email and password are required.");
      return;
    }
    setBusy(true);
    try {
      const res = await AdminService.cloneDemoAccount(form);
      if (res && res.error) {
        setError(res.error);
      } else {
        setForm({
          firstName: "",
          lastName: "",
          email: "",
          password: "",
          templateEmail: "",
          assignedLender: "",
          isDemo: true
        });
        load();
      }
    } catch (e2) {
      setError(String(e2 && e2.message ? e2.message : e2));
    } finally {
      setBusy(false);
    }
  }

  function startReassign(acct) {
    setReassignId(acct.userId);
    setReassign({
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      assignedLender: acct.assignedLender || "",
      isDemo: !!acct.isDemo
    });
    setError("");
  }

  async function submitReassign(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await AdminService.reassignDemoAccount({
        userId: reassignId,
        ...reassign
      });
      if (res && res.error) {
        setError(res.error);
      } else {
        setReassignId(null);
        load();
      }
    } catch (e2) {
      setError(String(e2 && e2.message ? e2.message : e2));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="demo-accounts">
      {error && <div className="demo-accounts-error">{error}</div>}

      <section className="demo-accounts-create">
        <h3>Create demo account for a new hire</h3>
        <p className="hint">
          Creates a Loan Officer login and copies the template account&#39;s loan
          files into their own private set. Default template is
          john.smith@lender.com.
        </p>
        <form onSubmit={submitClone}>
          <div className="da-row">
            <input
              placeholder="First name"
              value={form.firstName}
              onChange={e => setForm({ ...form, firstName: e.target.value })}
            />
            <input
              placeholder="Last name"
              value={form.lastName}
              onChange={e => setForm({ ...form, lastName: e.target.value })}
            />
          </div>
          <div className="da-row">
            <input
              placeholder="Login email"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
            />
            <input
              placeholder="Password"
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
            />
          </div>
          <div className="da-row">
            <input
              placeholder="Assigned Lender (e.g. UWM) — blank = all"
              value={form.assignedLender}
              onChange={e => setForm({ ...form, assignedLender: e.target.value })}
            />
            <input
              placeholder="Template email (optional)"
              value={form.templateEmail}
              onChange={e => setForm({ ...form, templateEmail: e.target.value })}
            />
            <label className="da-check">
              <input
                type="checkbox"
                checked={form.isDemo}
                onChange={e => setForm({ ...form, isDemo: e.target.checked })}
              />
              Demo account (export-all)
            </label>
            <button className="button" type="submit" disabled={busy}>
              {busy ? "Working…" : "Create Demo Account"}
            </button>
          </div>
        </form>
      </section>

      <section className="demo-accounts-list">
        <h3>Accounts</h3>
        {!accounts && <div>Loading…</div>}
        {accounts && accounts.length === 0 && <div>No accounts.</div>}
        {accounts && accounts.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Lender</th>
                <th>Demo</th>
                <th>Files</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {accounts.map(a => (
                <React.Fragment key={a.userId}>
                  <tr>
                    <td className="num">{a.userId}</td>
                    <td>{a.name}</td>
                    <td>{a.email}</td>
                    <td>{a.role || "—"}</td>
                    <td>{a.assignedLender || "—"}</td>
                    <td>{a.isDemo ? "Yes" : "—"}</td>
                    <td className="num">{a.docs}</td>
                    <td>
                      <button
                        className="button"
                        onClick={() => startReassign(a)}
                      >
                        Reassign
                      </button>
                    </td>
                  </tr>
                  {reassignId === a.userId && (
                    <tr className="reassign-row">
                      <td colSpan="8">
                        <form onSubmit={submitReassign} className="reassign-form">
                          <span className="reassign-title">
                            Reassign account {a.userId} (leave a field blank to
                            keep it):
                          </span>
                          <input
                            placeholder="New first name"
                            value={reassign.firstName}
                            onChange={e =>
                              setReassign({ ...reassign, firstName: e.target.value })
                            }
                          />
                          <input
                            placeholder="New last name"
                            value={reassign.lastName}
                            onChange={e =>
                              setReassign({ ...reassign, lastName: e.target.value })
                            }
                          />
                          <input
                            placeholder="New email"
                            value={reassign.email}
                            onChange={e =>
                              setReassign({ ...reassign, email: e.target.value })
                            }
                          />
                          <input
                            placeholder="New password"
                            value={reassign.password}
                            onChange={e =>
                              setReassign({ ...reassign, password: e.target.value })
                            }
                          />
                          <input
                            placeholder="Assigned Lender (blank = all)"
                            value={reassign.assignedLender}
                            onChange={e =>
                              setReassign({
                                ...reassign,
                                assignedLender: e.target.value
                              })
                            }
                          />
                          <label className="da-check">
                            <input
                              type="checkbox"
                              checked={reassign.isDemo}
                              onChange={e =>
                                setReassign({
                                  ...reassign,
                                  isDemo: e.target.checked
                                })
                              }
                            />
                            Demo (export-all)
                          </label>
                          <button className="button" type="submit" disabled={busy}>
                            Save
                          </button>
                          <button
                            className="button"
                            type="button"
                            onClick={() => setReassignId(null)}
                          >
                            Cancel
                          </button>
                        </form>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
